import React from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonSpinner,
  IonText,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonListHeader,
} from '@ionic/react';
import { useHistoryPage } from '../../application/use-cases/measurements/useHistoryPage';
import { describeCondition } from '../../domain/Alert';
import { componentDisplayName } from '../../domain/Component';
import type { Device } from '../../domain/Device';
import { METRIC_LABEL, type Measurement, type SensorMetric } from '../../domain/Measurement';
import { formatRelative } from '../../domain/WeatherStation';
import {
  thermometerOutline,
  waterOutline,
  speedometerOutline,
  rainyOutline,
  compassOutline,
  navigateOutline,
  leafOutline,
} from 'ionicons/icons';
import { skipReasonLabel, type IrrigationEvent } from '../../domain/Irrigation';
import IrrigationHistoryChart from '../components/IrrigationHistoryChart';
import './IrrigationHistory.css';
import './Sensors.css';
import './History.css';

function formatDuration(startIso: string, endIso: string | null): string {
  const start = new Date(startIso).getTime();
  const end = endIso ? new Date(endIso).getTime() : Date.now();
  const minutes = Math.max(1, Math.round((end - start) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function irrigationTitle(event: IrrigationEvent): string {
  if (event.status === 'skipped') return event.skipReason ? skipReasonLabel[event.skipReason] : 'Salteado';
  return event.triggerSource === 'manual' ? 'Riego manual' : 'Riego programado';
}

// "Humedad de suelo · Lote 4 30 cm" para una sonda; el resto, por su métrica.
function measurementLabel(m: Measurement, device: Device | undefined): string {
  if (m.metric !== 'soil_moisture') return METRIC_LABEL[m.metric];
  const probe = device?.components.find((c) => c.kind === 'soil' && c.channel === m.channel);
  return `${METRIC_LABEL[m.metric]} · ${probe ? componentDisplayName(probe) : `Sonda ${m.channel}`}`;
}

// Cómo se ve cada métrica en el historial: ícono y color de su tarjeta del Inicio.
const READING_LOOK: Record<SensorMetric, { icon: string; color: string; tint: string }> = {
  temperature: { icon: thermometerOutline, color: 'var(--domiot-metric-temp)', tint: 'var(--domiot-metric-temp-tint)' },
  relative_humidity: { icon: waterOutline, color: 'var(--domiot-metric-humidity)', tint: 'var(--domiot-metric-humidity-tint)' },
  pressure: { icon: speedometerOutline, color: 'var(--domiot-metric-pressure)', tint: 'var(--domiot-metric-pressure-tint)' },
  rain: { icon: rainyOutline, color: 'var(--domiot-metric-rain)', tint: 'var(--domiot-metric-rain-tint)' },
  wind_speed: { icon: compassOutline, color: 'var(--domiot-metric-wind)', tint: 'var(--domiot-metric-wind-tint)' },
  wind_direction: { icon: navigateOutline, color: 'var(--domiot-metric-wind)', tint: 'var(--domiot-metric-wind-tint)' },
  soil_moisture: { icon: leafOutline, color: 'var(--domiot-metric-soil)', tint: 'var(--domiot-metric-soil-tint)' },
};

// Las lecturas de un mismo mensaje (misma estación y momento) van juntas.
const MAX_MOMENTS = 40;
function readingGroups(list: Measurement[]) {
  const groups: { key: string; deviceId: string; recordedAt: string; items: Measurement[] }[] = [];
  for (const m of list) {
    const key = `${m.deviceId}|${m.recordedAt}`;
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(m);
    else if (groups.length < MAX_MOMENTS) groups.push({ key, deviceId: m.deviceId, recordedAt: m.recordedAt, items: [m] });
    else break;
  }
  return groups;
}

const History: React.FC = () => {
  const { devices, deviceFilter, setDeviceFilter, loading, error, sortedDesc, selectedDevice, deviceEvents, riegoEvents, dailyLiters } =
    useHistoryPage();
  const all = deviceFilter === 'all';
  const deviceById = (id: string) => devices.find((d) => d.id === id);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Historial</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {devices.length > 1 && (
          <div className="sensors-filter" role="tablist" aria-label="Filtrar por estación">
            <button role="tab" aria-selected={all} onClick={() => setDeviceFilter('all')}>
              Todas
            </button>
            {devices.map((d) => (
              <button key={d.id} role="tab" aria-selected={deviceFilter === d.id} onClick={() => setDeviceFilter(d.id)}>
                {d.name}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner color="primary" />
          </div>
        )}

        {!loading && error && (
          <IonText color="danger">
            <p className="ion-padding">{error}</p>
          </IonText>
        )}

        {!all && dailyLiters.length > 0 && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Riego · litros por día</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IrrigationHistoryChart dailyLiters={dailyLiters} />
            </IonCardContent>
          </IonCard>
        )}

        {riegoEvents.length > 0 && (
          <IonList className="irrigation-history-list history-list">
            <IonListHeader>
              <IonLabel>Riego</IonLabel>
            </IonListHeader>
            {riegoEvents.slice(0, all ? 30 : 100).map((event) => (
              <IonItem key={event.id} lines="full">
                <IonLabel>
                  <h3>
                    {all ? `${event.deviceName} · ` : ''}
                    {event.valveName} · {irrigationTitle(event)}
                  </h3>
                  <p>
                    {event.startedAt ? new Date(event.startedAt).toLocaleString('es-AR') : new Date().toLocaleDateString('es-AR')}
                    {event.status === 'running' && ' · en curso'}
                    {event.status !== 'running' && event.startedAt && ` · ${formatDuration(event.startedAt, event.endedAt)}`}
                  </p>
                </IonLabel>
                <IonLabel slot="end" className="ion-text-end">
                  {event.estimatedLiters !== null ? `${event.estimatedLiters} L` : ''}
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}

        {!all && deviceEvents.length > 0 && (
          <IonList className="history-list">
            <IonListHeader>
              <IonLabel>Alertas</IonLabel>
            </IonListHeader>
            {deviceEvents.map((event) => (
              <IonItem key={event.id} lines="full">
                <IonLabel>
                  <h3>{event.alertName ?? event.conditions.map((c) => describeCondition(c)).join(' y ')}</h3>
                  <p>{event.conditions.map((c) => describeCondition(c, c.value)).join(' y ')}</p>
                  <p>
                    {new Date(event.startedAt).toLocaleString('es-AR')}
                    {event.endedAt ? ` · ${formatDuration(event.startedAt, event.endedAt)}` : ' · en curso'}
                  </p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}

        {!loading && !error && (
          <section className="history-timeline">
            <p className="history-heading">Últimas lecturas</p>
            {readingGroups(sortedDesc).map((group) => {
              const device = selectedDevice ?? deviceById(group.deviceId);
              return (
                <div className="history-moment" key={group.key}>
                  <span className="history-dot" aria-hidden="true" />
                  <div className="history-moment-card">
                    <div className="history-moment-head">
                      <strong>{formatRelative(group.recordedAt)}</strong>
                      <span>
                        {all ? `${group.items[0].deviceName ?? device?.name ?? ''} · ` : ''}
                        {new Date(group.recordedAt).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="history-readings">
                      {group.items.map((m) => {
                        const look = READING_LOOK[m.metric];
                        return (
                          <span key={`${m.metric}-${m.channel}`} className="history-reading" style={{ '--tone': look.color, '--tone-tint': look.tint } as React.CSSProperties}>
                            <IonIcon icon={look.icon} />
                            <span>
                              <strong>
                                {m.value}
                                {m.unit === '°C' || m.unit === '%' || m.unit === '°' ? m.unit : ` ${m.unit}`}
                              </strong>
                              <small>{measurementLabel(m, device)}</small>
                            </span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
            {sortedDesc.length === 0 && <p className="history-empty">Sin lecturas todavía.</p>}
          </section>
        )}
      </IonContent>
    </IonPage>
  );
};

export default History;
